/**
 * AI model catalog (AB#669) — the models Baxter's picker can offer for a site:
 * the free OpenRouter tier (tool-calling models only, `services/ai_models.rs`)
 * plus one entry per BYOK provider key the caller can see for that site.
 */

import { api } from './client';
import type { ApiResponse } from '../types';

/** Observed OpenRouter catalog base rates, not a quote for a particular request. */
export interface AiModelPricing {
  source: 'openrouter';
  currency: 'USD';
  prompt_usd_per_token: number | null;
  completion_usd_per_token: number | null;
  observed_at: string;
  conditional: boolean;
}

/** One selectable chat model. `provider` is a registry id (see AiProviderId in aiKeys). */
export interface AiModel {
  id: string;
  name: string;
  context_length: number;
  /** True only on the platform OpenRouter key's free tier — see the free-model guarantee. */
  free: boolean;
  /** Baxter needs tool calling; the catalog only ever contains models where this is true. */
  tools: boolean;
  provider: string;
  /** The model can read images. */
  vision?: boolean;
  /** 1-5 ratings from the backend; null = unrated (e.g. `openrouter/free`, which is a router). */
  strength?: number | null;
  speed?: number | null;
  pricing?: AiModelPricing | null;
}

export const aiModelsApi = {
  /**
   * Models selectable in this site's context. Same `/api/v1/ai` prefix and
   * `?site_slug=` convention as aiKeysApi (both new AB#669 endpoints, not the
   * older per-site-nested `/v1/sites/{slug}/ai/*` chat routes).
   *
   * `siteSlug` is optional: the New Site wizard (AB#642) picks a model before a
   * site exists, and the backend already resolves personal/account keys without it.
   */
  list: (siteSlug?: string) =>
    api
      .get<ApiResponse<AiModel[]>>(
        siteSlug ? `/v1/ai/models?site_slug=${encodeURIComponent(siteSlug)}` : '/v1/ai/models',
      )
      .then((res) => res.data ?? []),

  /**
   * Re-pull each visible BYOK provider's catalog now, rather than waiting for
   * the server snapshot's TTL — a provider that just shipped a model is missing
   * from `list` until then. `failed` counts providers that wouldn't answer
   * (their previous entries are kept).
   */
  refresh: (siteSlug?: string) =>
    api
      .post<ApiResponse<{ refreshed: number; failed: number }>>(
        siteSlug
          ? `/v1/ai/models/refresh?site_slug=${encodeURIComponent(siteSlug)}`
          : '/v1/ai/models/refresh',
        {},
      )
      .then((res) => res.data ?? { refreshed: 0, failed: 0 }),
};

export interface AiPreferences { models: string[]; providers: string[]; can_edit: boolean }
const preferencesPath = (siteSlug?: string) => '/v1/ai/preferences' + (siteSlug ? `?site_slug=${encodeURIComponent(siteSlug)}` : '');
export const aiPreferencesApi = {
  get: (siteSlug?: string) => api.get<ApiResponse<AiPreferences>>(preferencesPath(siteSlug)).then((res) => res.data!),
  update: (preferences: Pick<AiPreferences, 'models' | 'providers'>, siteSlug?: string) =>
    api.put<ApiResponse<AiPreferences>>(preferencesPath(siteSlug), preferences).then((res) => res.data!),
};
